import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'

export type VoiceControlState = {
  supported: boolean
  enabled: boolean
  running: boolean
  phase: string
  error: string | null
  pending: number
}

type WakePlugin = {
  getState(): Promise<unknown>
  enable(): Promise<unknown>
  disable(): Promise<unknown>
  suspend(): Promise<unknown>
  resume(): Promise<unknown>
  recoverRecording(): Promise<unknown>
  addListener(event: 'stateChange' | 'conversationReady', listener: (value: unknown) => void): Promise<PluginListenerHandle>
}

const wake = registerPlugin<WakePlugin>('AgentChatWake')
export function isWakeVoiceAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AgentChatWake')
}
let snapshot: VoiceControlState = {
  supported: isWakeVoiceAvailable(), enabled: false, running: false, phase: 'off', error: null, pending: 0,
}
const subscribers = new Set<() => void>()
function update(values: Partial<VoiceControlState>) {
  snapshot = { ...snapshot, ...values }
  for (const subscriber of subscribers) subscriber()
}
function acceptState(value: unknown) {
  if (typeof value !== 'object' || value === null || !('enabled' in value) || typeof value.enabled !== 'boolean'
    || !('running' in value) || typeof value.running !== 'boolean' || !('phase' in value) || typeof value.phase !== 'string') {
    throw new Error('The Android voice service returned an unreadable state. Install the latest app update.')
  }
  update({ supported: true, enabled: value.enabled, running: value.running, phase: value.phase,
    error: 'error' in value && typeof value.error === 'string' && value.error ? value.error : null,
    pending: 'pending' in value && typeof value.pending === 'number' && Number.isFinite(value.pending) ? Math.max(0, value.pending) : 0 })
}
export const getVoiceControlState = () => snapshot
export function subscribeVoiceControl(subscriber: () => void): () => void {
  subscribers.add(subscriber)
  return () => { subscribers.delete(subscriber) }
}
export async function refreshVoiceControl(): Promise<void> {
  if (!isWakeVoiceAvailable()) { update({ supported: false }); return }
  acceptState(await wake.getState())
}
export async function setVoiceControlEnabled(enabled: boolean): Promise<void> {
  if (!isWakeVoiceAvailable()) throw new Error('Screen-off voice control needs the latest AgentChat Android app.')
  acceptState(await (enabled ? wake.enable() : wake.disable()))
  nativePaused = false
  if (enabled && microphoneUsers > 0) await syncMicrophone()
}
export async function recoverVoiceRecording(): Promise<void> {
  if (!isWakeVoiceAvailable()) throw new Error('Install the latest AgentChat Android app to recover recordings.')
  await wake.recoverRecording()
}
export function wakeConversationRoute(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('url' in value) || typeof value.url !== 'string') return null
  if (!value.url.startsWith('/chats/pwa-auto-code-codex?')) return null
  const params = new URLSearchParams(value.url.split('?')[1])
  const id = params.get('topic_id')
  if (!id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return null
  return `/chats/pwa-auto-code-codex?tab=code&chat_id=pwa-auto-code-codex&topic_id=${id}`
}
export function bindVoiceControl(onConversation: (route: string) => void): () => void {
  if (!isWakeVoiceAvailable()) return () => undefined
  let disposed = false
  const handles: PluginListenerHandle[] = []
  const add = (event: 'stateChange' | 'conversationReady', listener: (value: unknown) => void) => {
    void wake.addListener(event, (value) => { if (!disposed) listener(value) }).then((handle) => {
      if (disposed) void handle.remove()
      else handles.push(handle)
    }).catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Could not observe Android voice control' }))
  }
  add('stateChange', (value) => {
    try { acceptState(value) } catch (error) { update({ error: error instanceof Error ? error.message : 'Could not read voice state' }) }
  })
  add('conversationReady', (value) => {
    const route = wakeConversationRoute(value)
    if (route && document.visibilityState === 'visible') onConversation(route)
  })
  const refresh = () => { if (document.visibilityState === 'visible') void refreshVoiceControl().catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Could not read voice state' })) }
  refresh()
  document.addEventListener('visibilitychange', refresh)
  return () => {
    disposed = true
    document.removeEventListener('visibilitychange', refresh)
    for (const handle of handles) void handle.remove()
  }
}

let microphoneUsers = 0
let nativePaused = false
let microphoneChange = Promise.resolve()
function syncMicrophone(): Promise<void> {
  const next = microphoneChange.catch(() => undefined).then(async () => {
    if (microphoneUsers > 0 && !nativePaused) { await wake.suspend(); nativePaused = true }
    else if (microphoneUsers === 0 && nativePaused) { await wake.resume(); nativePaused = false }
  })
  microphoneChange = next
  return next
}
export async function acquireVoiceMicrophone(): Promise<() => void> {
  if (!isWakeVoiceAvailable()) return () => undefined
  microphoneUsers += 1
  try { await syncMicrophone() } catch (error) { microphoneUsers -= 1; throw error }
  let released = false
  return () => {
    if (released) return
    released = true
    microphoneUsers -= 1
    void syncMicrophone().catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Wake listening could not resume. Reopen AgentChat.' }))
  }
}
